import { useEffect, useState, useCallback } from "react";
import { Search, PackageOpen, Loader2, X, ArrowLeft } from "lucide-react";
import { api, formatMoney, effectivePrice, priceOptions } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import useProgressiveList from "@/hooks/useProgressiveList";

/**
 * mode: "products" | "offers" | "pricelist"
 */
export default function ProductsSection({ mode = "products", brandFilter, onClearBrand }) {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [added, setAdded] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (brandFilter) params.brand = brandFilter;
    if (mode === "offers") params.offers_only = true;
    api
      .get("/products", { params })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [brandFilter, mode]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = (product, qty, option) => {
    addItem(product, qty, option);
    setAdded(product.name);
    setTimeout(() => setAdded(null), 1800);
  };

  const filtered = products.filter((p) => {
    const q = query.trim().toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q);
  });

  const { visible, sentinelRef, hasMore } = useProgressiveList(filtered, 24, `${mode}|${brandFilter || ""}|${query.trim().toLowerCase()}`);

  const titles = {
    products: "Products",
    offers: "Offers",
    pricelist: "Price List",
  };
  const emptyText = {
    products: "No products yet. Please check back soon.",
    offers: "No offers running right now. Check back soon!",
    pricelist: "The price list will appear here once products are added.",
  };

  return (
    <div data-testid={`${mode}-section`}>
      {brandFilter && (
        <button
          data-testid="back-to-brands-button"
          onClick={onClearBrand}
          className="mb-3 inline-flex w-full items-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-3 text-sm font-bold text-blue-700 shadow-sm transition hover:bg-blue-50 active:scale-[0.99] sm:w-auto"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" />
          <span className="truncate">Our Brands</span>
        </button>
      )}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">{titles[mode]}</h2>
          {brandFilter ? (
            <button
              data-testid="clear-brand-filter"
              onClick={onClearBrand}
              className="mt-1 inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-200"
            >
              {brandFilter}
              <X className="h-3 w-3" />
            </button>
          ) : (
            <p className="text-sm text-slate-500">{filtered.length} items</p>
          )}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={`${mode}-search-input`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products..."
            className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      {added && (
        <div className="mb-3 rounded-lg bg-green-50 px-4 py-2 text-sm font-medium text-green-700">
          Added "{added}" to cart.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center text-slate-400">
          <PackageOpen className="h-10 w-10" />
          <p className="text-sm">{emptyText[mode]}</p>
        </div>
      ) : mode === "pricelist" ? (
        <div className="overflow-hidden rounded-xl border border-blue-100 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-blue-50 text-xs uppercase tracking-wide text-blue-700">
              <tr>
                <th className="px-4 py-3">Product</th>
                <th className="hidden px-4 py-3 sm:table-cell">Brand</th>
                <th className="px-4 py-3 text-right">Price</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const opts = priceOptions(p);
                return (
                  <tr key={p.id} data-testid={`pricelist-row-${p.id}`} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {p.name}
                      {p.pack_size ? <span className="text-slate-400"> · {p.pack_size}</span> : null}
                    </td>
                    <td className="hidden px-4 py-3 text-slate-500 sm:table-cell">{p.brand}</td>
                    <td className="px-4 py-3 text-right">
                      {opts.length === 0 ? (
                        <span className="text-slate-400">On request</span>
                      ) : (
                        <div className="flex flex-col items-end gap-0.5">
                          {opts.map((o, i) => (
                            <span key={i} className="text-sm">
                              <span className="text-slate-400">{o.label}:</span>{" "}
                              <span className="font-semibold text-blue-700">{formatMoney(o.rate)}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {visible.map((p) => (
            <ProductCard key={p.id} product={p} onAdd={handleAdd} />
          ))}
          {hasMore && <div ref={sentinelRef} data-testid={`${mode}-load-more`} className="col-span-full flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-blue-400" /></div>}
        </div>
      )}
    </div>
  );
}
